import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAccount, useBalance, useChainId, useDisconnect, useSignMessage, useSwitchChain } from "wagmi";
import { WsProvider } from "../hooks/useWsConnection";
import {
  apiAuthNonce,
  apiAuthVerify,
  apiAuthLogout,
  apiCheckin,
  apiConnectWallet,
  apiGetMarketAssets,
  apiHealth,
  apiRecordAction,
  apiUserDashboard,
  saveToken,
  clearToken,
  getToken,
} from "../api/backend";
import { chainNameById, chainSymbolById } from "../utils/chains";
import { stripLangFromPath, buildPath } from "../utils/routing";

const AppStateContext = createContext(null);

const EMPTY_DASHBOARD = {
  holdings: [],
  tradeHistory: [],
  pendingOrders: [],
  cashflows: [],
  recentActions: [],
  points: 0,
  streak: 0,
};

function normalizeCheckinLog(actions = []) {
  return actions
    .filter((item) => item?.actionType === "checkin")
    .map((item) => ({
      time: new Date(item.time || Date.now()).toISOString(),
      status: item.status || "SUCCESS",
      reason: item.txHash ? `${item.note || "CHECKIN"} | ${item.txHash}` : item.note || "CHECKIN",
      points: Number(item.points || 0),
    }));
}

export function AppStateProvider({ children }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { address, isConnected, chain: connectedChain } = useAccount();
  const { disconnectAsync } = useDisconnect();
  const { signMessageAsync } = useSignMessage();
  const { switchChainAsync } = useSwitchChain();
  const { openConnectModal } = useConnectModal();
  const chainId = useChainId();

  // Use the actual connected chain ID from the wallet, not the default from config
  const actualChainId = connectedChain?.id || chainId;

  const { data: walletBalance } = useBalance({
    address,
    query: { enabled: Boolean(address) },
  });

  const [dashboard, setDashboard] = useState(EMPTY_DASHBOARD);
  const [authStatus, setAuthStatus] = useState({
    state: "idle",
    address: "",
    message: "",
    verifiedAt: "",
  });
  const authInFlightRef = useRef(false);
  const lastAuthenticatedAddressRef = useRef("");
  const autoAuthAttemptedAddressRef = useRef("");
  const wasWalletLinkedRef = useRef(false);

  const navigateToForbidden = useCallback(() => {
    const { locale } = stripLangFromPath(location.pathname);
    navigate(buildPath(locale, "forbidden"), { replace: true });
  }, [navigate, location.pathname]);

  const walletAddress = address || "";
  const walletLinked = Boolean(isConnected && walletAddress);
  const walletConnected = Boolean(
    walletLinked &&
      authStatus.state === "success" &&
      authStatus.address.toLowerCase() === walletAddress.toLowerCase(),
  );
  const network = chainNameById(actualChainId);
  const balance = Number(walletBalance?.formatted || 0);
  const balanceFormatted = String(walletBalance?.formatted || "0");
  const balanceWei = walletBalance?.value ?? 0n;
  const balanceSymbol = walletBalance?.symbol || chainSymbolById(actualChainId);

  const healthQuery = useQuery({
    queryKey: ["backend", "health"],
    queryFn: apiHealth,
    staleTime: 300_000,
    retry: 1,
  });

  const assetsQuery = useQuery({
    queryKey: ["market", "assets"],
    queryFn: () => apiGetMarketAssets(false),
    staleTime: 20_000,
    retry: 1,
  });

  const assets = useMemo(() => {
    return Array.isArray(assetsQuery.data?.assets) ? assetsQuery.data.assets : [];
  }, [assetsQuery.data]);

  const dashboardQuery = useQuery({
    queryKey: ["user", "dashboard", walletAddress],
    queryFn: () => apiUserDashboard(walletAddress),
    enabled: Boolean(walletAddress && walletConnected),
    staleTime: 15_000,
    retry: 1,
  });

  const assetsLoading = assetsQuery.isPending || assetsQuery.isFetching;
  const assetsError = assetsQuery.error ? String(assetsQuery.error?.message || assetsQuery.error) : "";

  const backendStatus = useMemo(() => {
    if (healthQuery.data) {
      return {
        loading: false,
        online: true,
        chainId: healthQuery.data.chainId ?? null,
        latestBlock: healthQuery.data.latestBlock ?? null,
        error: "",
      };
    }

    if (healthQuery.isPending) {
      return {
        loading: true,
        online: false,
        chainId: null,
        latestBlock: null,
        error: "",
      };
    }

    return {
      loading: false,
      online: false,
      chainId: null,
      latestBlock: null,
      error: String(healthQuery.error?.message || healthQuery.error || ""),
    };
  }, [healthQuery.data, healthQuery.error, healthQuery.isPending]);

  const refreshHealth = useCallback(async () => {
    const result = await healthQuery.refetch();
    if (result.error) throw result.error;
    return result.data;
  }, [healthQuery]);

  const refreshAssets = useCallback(
    async (forceRefresh = false) => {
      if (!forceRefresh) {
        const result = await assetsQuery.refetch();
        if (result.error) {
          return [];
        }
        return Array.isArray(result.data?.assets) ? result.data.assets : [];
      }

      try {
        const freshResult = await apiGetMarketAssets(true);
        queryClient.setQueryData(["market", "assets"], freshResult);
        return Array.isArray(freshResult?.assets) ? freshResult.assets : [];
      } catch {
        return [];
      }
    },
    [assetsQuery, queryClient],
  );

  const refreshDashboard = useCallback(
    async (targetAddress = walletAddress) => {
      if (!targetAddress) return EMPTY_DASHBOARD;
      const result = await queryClient.fetchQuery({
        queryKey: ["user", "dashboard", targetAddress],
        queryFn: () => apiUserDashboard(targetAddress),
        staleTime: 0,
      });
      const next = result?.dashboard || EMPTY_DASHBOARD;
      setDashboard(next);
      return next;
    },
    [queryClient, walletAddress],
  );

  const checkinMutation = useMutation({
    mutationFn: ({ address: targetAddress, payload }) =>
      apiCheckin(targetAddress, payload),
    onSuccess: (result, variables) => {
      if (result?.dashboard) {
        setDashboard(result.dashboard);
        queryClient.setQueryData(["user", "dashboard", variables.address], {
          dashboard: result.dashboard,
        });
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ["user", "dashboard", variables.address],
      });
    },
  });

  const recordActionMutation = useMutation({
    mutationFn: ({ address: targetAddress, actionType, payload }) =>
      apiRecordAction(targetAddress, actionType, payload),
    onSuccess: (result, variables) => {
      if (result?.dashboard) {
        setDashboard(result.dashboard);
        queryClient.setQueryData(["user", "dashboard", variables.address], {
          dashboard: result.dashboard,
        });
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ["user", "dashboard", variables.address],
      });
    },
  });

  const authenticateWallet = useCallback(async (options = {}) => {
    const silent = Boolean(options?.silent);

    if (!walletAddress) {
      throw new Error("wallet not connected");
    }
    if (authInFlightRef.current) {
      return;
    }

    authInFlightRef.current = true;
    setAuthStatus({
      state: "loading",
      address: walletAddress,
      message: "Authenticating...",
      verifiedAt: "",
    });

    try {
      const nonceResult = await apiAuthNonce(walletAddress);
      
      // Always ensure we're on BNB Chain (chainId 56) before signing
      try {
        await switchChainAsync({ chainId: 56 });
        // Wait for the chain switch to fully propagate in the wallet
        await new Promise(resolve => setTimeout(resolve, 1500));
      } catch (switchError) {
        // Continue anyway, might already be on the correct chain
      }
      
      const signature = await signMessageAsync({ message: nonceResult.message });
      
      const verifyResult = await apiAuthVerify(walletAddress, nonceResult.nonce, signature);
      
      // Save JWT token
      if (verifyResult.token) {
        saveToken(verifyResult.token);
      }
      
      const connectResult = await apiConnectWallet(walletAddress);

      const nextDashboard = connectResult?.dashboard || EMPTY_DASHBOARD;
      setDashboard(nextDashboard);
      queryClient.setQueryData(["user", "dashboard", walletAddress], {
        dashboard: nextDashboard,
      });
      lastAuthenticatedAddressRef.current = walletAddress.toLowerCase();
      
      const verifiedAt = verifyResult?.verifiedAt || new Date().toISOString();
      
      setAuthStatus({
        state: "success",
        address: walletAddress,
        message: "Authenticated",
        verifiedAt,
      });
      return connectResult;
    } catch (error) {
      // Region-blocked: navigate to forbidden page and abort auth state changes
      if (error?.regionBlocked) {
        navigateToForbidden();
        return;
      }
      if (silent) {
        setAuthStatus((prev) => {
          if (
            prev.state === "success" &&
            prev.address.toLowerCase() === walletAddress.toLowerCase()
          ) {
            return prev;
          }
          return {
            state: "idle",
            address: walletAddress,
            message: "",
            verifiedAt: "",
          };
        });
      } else {
        setAuthStatus({
          state: "error",
          address: walletAddress,
          message: String(error?.message || error),
          verifiedAt: "",
        });
      }
      throw error;
    } finally {
      authInFlightRef.current = false;
    }
  }, [walletAddress, signMessageAsync, switchChainAsync, navigateToForbidden, queryClient]);

  const connectWallet = useCallback(async () => {
    if (!walletLinked) {
      openConnectModal?.();
      return;
    }
    if (!walletConnected) {
      await authenticateWallet();
    }
  }, [authenticateWallet, openConnectModal, walletConnected, walletLinked]);

  const disconnectWallet = useCallback(async () => {
    if (getToken()) {
      try {
        await apiAuthLogout();
      } catch {
        // ignore logout API failure
      }
    }

    try {
      await disconnectAsync();
    } finally {
      lastAuthenticatedAddressRef.current = "";
      clearToken();
      queryClient.removeQueries({ queryKey: ["user", "dashboard"] });
      setDashboard(EMPTY_DASHBOARD);
      setAuthStatus({
        state: "idle",
        address: "",
        message: "",
        verifiedAt: "",
      });
    }
  }, [disconnectAsync, queryClient]);

  const runCheckinApi = useCallback(async (payload = {}) => {
    if (!walletConnected || !walletAddress) {
      throw new Error("wallet not authenticated");
    }
    const result = await checkinMutation.mutateAsync({
      address: walletAddress,
      payload,
    });
    if (!result?.dashboard) {
      await refreshDashboard(walletAddress);
    }
    return result;
  }, [checkinMutation, refreshDashboard, walletAddress, walletConnected]);

  const recordActionApi = useCallback(
    async (actionType, payload = {}) => {
      if (!walletConnected || !walletAddress) {
        throw new Error("wallet not authenticated");
      }
      const result = await recordActionMutation.mutateAsync({
        address: walletAddress,
        actionType,
        payload,
      });
      if (!result?.dashboard) {
        await refreshDashboard(walletAddress);
      }
      return result;
    },
    [recordActionMutation, refreshDashboard, walletAddress, walletConnected],
  );

  useEffect(() => {
    if (dashboardQuery.data?.dashboard) {
      setDashboard(dashboardQuery.data.dashboard);
    }
  }, [dashboardQuery.data]);

  useEffect(() => {
    const justLinked = !wasWalletLinkedRef.current && walletLinked;
    wasWalletLinkedRef.current = walletLinked;

    if (!justLinked) {
      return;
    }
    
    // Check if we have a valid token
    const token = getToken();
    if (token && walletAddress) {
      // Immediately mark as attempted to prevent duplicate auth in the second useEffect
      const normalized = walletAddress.toLowerCase();
      autoAuthAttemptedAddressRef.current = normalized;
      lastAuthenticatedAddressRef.current = normalized;
      
      setAuthStatus({
        state: "loading",
        address: walletAddress,
        message: "Restoring session...",
        verifiedAt: "",
      });
      
      // Try to load dashboard with existing token
      refreshDashboard(walletAddress)
        .then(() => {
          setAuthStatus({
            state: "success",
            address: walletAddress,
            message: "Authenticated",
            verifiedAt: new Date().toISOString(),
          });
        })
        .catch(() => {
          // Token expired or invalid, clear it and reset auth
          clearToken();
          lastAuthenticatedAddressRef.current = "";
          autoAuthAttemptedAddressRef.current = "";
          setAuthStatus({
            state: "idle",
            address: walletAddress || "",
            message: "",
            verifiedAt: "",
          });
        });
    } else {
      // No token, reset auth state
      lastAuthenticatedAddressRef.current = "";
      autoAuthAttemptedAddressRef.current = "";
      setAuthStatus((prev) => {
        if (prev.state === "loading") return prev;
        return {
          state: "idle",
          address: walletAddress || "",
          message: "",
          verifiedAt: "",
        };
      });
    }

    // Always try to switch to BNB Chain after connection (async, non-blocking)
    (async () => {
      try {
        await switchChainAsync({ chainId: 56 });
        // Wait for chain switch to fully propagate
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        // It's ok if switch fails when already on the correct chain
      }
    })();
  }, [walletAddress, walletLinked, switchChainAsync, refreshDashboard]);

  useEffect(() => {
    if (healthQuery.error?.regionBlocked || assetsQuery.error?.regionBlocked) {
      navigateToForbidden();
    }
  }, [assetsQuery.error, healthQuery.error, navigateToForbidden]);

  useEffect(() => {
    if (!walletLinked || !walletAddress) {
      lastAuthenticatedAddressRef.current = "";
      autoAuthAttemptedAddressRef.current = "";
      clearToken();
      setDashboard(EMPTY_DASHBOARD);
      setAuthStatus({
        state: "idle",
        address: "",
        message: "",
        verifiedAt: "",
      });
      return;
    }

    const normalized = walletAddress.toLowerCase();
    const isVerifiedForCurrentAddress =
      authStatus.state === "success" && authStatus.address.toLowerCase() === normalized;

    if (isVerifiedForCurrentAddress) {
      lastAuthenticatedAddressRef.current = normalized;
      return;
    }

    if (authStatus.state === "loading") {
      return;
    }

    if (autoAuthAttemptedAddressRef.current === normalized) {
      return;
    }

    autoAuthAttemptedAddressRef.current = normalized;

    authenticateWallet({ silent: true }).catch(() => {});
  }, [authenticateWallet, authStatus.address, authStatus.state, walletAddress, walletLinked]);

  const checkinLog = useMemo(
    () => normalizeCheckinLog(dashboard?.recentActions || []),
    [dashboard?.recentActions],
  );

  const value = {
    walletLinked,
    walletConnected,
    walletAddress,
    authStatus,
    network,
    chainId: actualChainId, // Use actual wallet chain ID
    balance,
    balanceFormatted,
    balanceWei,
    balanceSymbol,
    backendStatus,
    dashboard,
    assets,
    assetsLoading,
    assetsError,
    pendingOrders: dashboard?.pendingOrders || [],
    tradeLogs: dashboard?.tradeHistory || [],
    checkinLog,
    connectWallet,
    disconnectWallet,
    authenticateWallet,
    refreshDashboard,
    refreshAssets,
    refreshHealth,
    runCheckinApi,
    recordActionApi,
  };

  return (
    <WsProvider walletAddress={walletAddress} enabled={true}>
      <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>
    </WsProvider>
  );
}

export function useAppState() {
  const ctx = useContext(AppStateContext);
  if (!ctx) throw new Error("useAppState must be used inside AppStateProvider");
  return ctx;
}
